import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Grid,
  GridItem,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  useToast,
  Text,
  Select,
  InputGroup,
  InputRightElement,
  VisuallyHidden,
} from "@chakra-ui/react";
import { FiCheck, FiEdit2, FiPlus, FiTrash2, FiUsers } from "react-icons/fi";

import {
  ContactInfo,
  ContactsInfo,
} from "../../../../types/registerBranchDataTypes";
import { useState } from "react";
import { useBranch } from "../../../../data/user";
import { DataTable, EmptyState } from "../../../ui";

interface contactsStepProps {
  savedData: ContactsInfo;
  updateData: (data:ContactsInfo)=> void;
}

interface UpdatedContactsInfo extends ContactInfo {
  index: number;
}

const titleOptions = [
  "Owner",
  "Manager",
  "Partner",
  "Finance Manager",
  "Accounts Payable",
  "Purchasing Manager",
];
const ContactsStep: React.FC<contactsStepProps> = ({
  savedData,
  updateData,
}) => {
  const [selectedUser, setSelectedUser] = useState<UpdatedContactsInfo>({
    title: "",
    name: "",
    email: "",
    phone: "",
    mobile: "",
    shareholder_percentage: "",
    index: -1,
  });

  const { branch } = useBranch();

  const [editEnabled, setEditEnabled] = useState<boolean>(false);
  const toast = useToast();

  return (
    <Box>
      {savedData && savedData.length > 0 ? (
        <DataTable size="sm" mb={6} label="Contacts table">
          <Table>
            <Thead>
              <Tr>
                <Th scope="col">Title</Th>
                <Th scope="col">Name</Th>
                <Th scope="col">Email</Th>
                <Th scope="col">Phone</Th>
                <Th scope="col">Mobile</Th>
                <Th scope="col" isNumeric>Shareholder Percentage</Th>
                <Th scope="col">
                  <VisuallyHidden>Actions</VisuallyHidden>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {savedData.map((user, index) => (
                <Tr key={index}>
                  <Td>{user.title}</Td>
                  <Td fontWeight={600}>{user.name}</Td>
                  <Td>{user.email}</Td>
                  <Td>{user.phone}</Td>
                  <Td>{user.mobile}</Td>
                  <Td isNumeric>{user.shareholder_percentage}</Td>
                  <Td>
                    <HStack spacing={2} justify="flex-end">
                      <Button
                        variant="secondary"
                        size="xs"
                        leftIcon={<FiEdit2 />}
                        onClick={() => {
                          setSelectedUser({ ...user, index: index });
                          setEditEnabled(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger-soft"
                        size="xs"
                        leftIcon={<FiTrash2 />}
                        onClick={() => {
                          updateData(savedData.filter((_, i) => i !== index));
                        }}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DataTable>
      ) : (
        <EmptyState
          icon={FiUsers}
          title="No contacts added"
          description='Fill in the form below and press "Add" to list your trade licence members.'
          py={6}
          headingAs="p"
        />
      )}
      <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={5}>
        <GridItem colSpan={1}>
          <FormControl
            isInvalid={!selectedUser?.title} // validation
          >
            <FormLabel>Title</FormLabel>
            <Select
              placeholder="Select Title"
              value={selectedUser?.title}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  title: e.target.value,
                });
              }}
            >
              {titleOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input
              type="text"
              isInvalid={!selectedUser?.name} // validation
              value={selectedUser?.name}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  name: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Phone </FormLabel>
            <Input
              isInvalid={!selectedUser?.phone} // validation
              value={selectedUser?.phone}
              placeholder={branch === "UAE" ? "041234567": "Enter phone number"}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  phone: e.target.value,
                });
              }}
              />
          </FormControl>
        </GridItem>

        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              isInvalid={!selectedUser?.email} // validation
              placeholder={branch === "UAE" ? "test@test.com": "Enter email address"}
              value={selectedUser?.email}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  email: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Mobile </FormLabel>
            <Input
              value={selectedUser?.mobile}
              placeholder={branch === "UAE" ? "0501234567": "Enter mobile number"}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  mobile: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Shareholder Percentage</FormLabel>
            <InputGroup>
              <Input
                value={selectedUser?.shareholder_percentage}
                onChange={(e) => {
                  setSelectedUser({
                    ...selectedUser,
                    shareholder_percentage: e.target.value,
                  });
                }}
                pr="2.5rem" // add some padding to prevent overlap with the % symbol
              />
              <InputRightElement pointerEvents="none" width="2.5rem">
                <Text as="span" textStyle="caption" color="text.muted">
                  %
                </Text>
              </InputRightElement>
            </InputGroup>
          </FormControl>
        </GridItem>
        <GridItem colSpan={1} display="flex" alignItems="flex-end">
          <Button
            w="full"
            variant="secondary"
            leftIcon={editEnabled ? <FiCheck /> : <FiPlus />}
            isDisabled={
              !selectedUser?.name ||
              !selectedUser?.title ||
              !selectedUser?.email ||
              !selectedUser?.phone
            }
            onClick={() => {
              if (editEnabled) {
                // update the data and don't forget to remove the index  but after updating the data
                const { index, ...newUser } = selectedUser;
                updateData(
                  savedData.map((user, i) => (i === index ? newUser : user))
                );

                toast({
                  title: "Contact Updated",
                  description: "Contact has been updated successfully",
                  status: "warning",
                  duration: 9000,
                  isClosable: true,
                });

                setEditEnabled(false);
              } else {
                // remove the index before updating the data
                const { index, ...newUser } = selectedUser;
                updateData([...savedData, newUser]);

                toast({
                  title: "Contact Added",
                  description: "Contact has been added successfully",
                  status: "success",
                  duration: 9000,
                  isClosable: true,
                });
              }
              setSelectedUser({
                title: "",
                name: "",
                email: "",
                phone: "",
                mobile: "",
                shareholder_percentage: "",
                index: -1,
              });
            }}
          >
            {editEnabled ? "Save" : "Add"}
          </Button>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default ContactsStep;
